import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { useCommonContext } from "../../../utils/context";
import { useTypeConfig } from "../../../utils/useTypeConfig";

const ServiceExpenseModal = ({
  show,
  handleClose,
  technicianId,
  fetchServiceRequests,
  serviceExpenseData,
  isStatusUpdate,
}) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const { customers, tickets, stocks } = useCommonContext();
  const { labels } = useTypeConfig();

  const [requestType, setRequestType] = useState("ticket");
  const [subCustomers, setSubCustomers] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSubCustomer, setSelectedSubCustomer] = useState(null);

  const [currentStatus, setCurrentStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "material_required",
  });

  useEffect(() => {
    if (serviceExpenseData) {
      setCurrentStatus(serviceExpenseData.status || "");

      if (serviceExpenseData.ticket) {
        setRequestType("ticket");
        setValue("ticket", serviceExpenseData.ticket?.id || "");
      } else {
        setRequestType("customer");
        setSelectedCustomer(
          serviceExpenseData.customerDetails
            ? {
                value: serviceExpenseData.customerDetails.id,
                label: serviceExpenseData.customerDetails.customer_name,
              }
            : null,
        );
        setSelectedSubCustomer(
          serviceExpenseData.subCustomerDetails
            ? {
                value: serviceExpenseData.subCustomerDetails.id,
                label: serviceExpenseData.subCustomerDetails.customer_name,
              }
            : null,
        );

        if (serviceExpenseData.customerDetails?.id) {
          fetchSubCustomersTec(serviceExpenseData.customerDetails.id);
        }
      }

      if (serviceExpenseData.material_required?.length > 0) {
        replace(
          serviceExpenseData.material_required.map((mat) => {
            const matchedStock = stocks?.find((s) => s.item === mat.name);

            return {
              name: mat.name || "",
              stock_id: matchedStock?.id || null,
              quantity: mat.quantity || 1,
            };
          }),
        );
      } else {
        replace([{ name: "", quantity: 1 }]);
      }
    } else {
      replace([{ name: "", quantity: 1 }]);
    }
  }, [serviceExpenseData, setValue, replace]);

  const fetchSubCustomersTec = async (customerId) => {
    if (!customerId) return;
    try {
      const { data } = await axios.get(
        `${BASE_URL}/users/tms_users/?role=subcustomer&customer_id=${customerId}&page_size=all`,
        { headers: HEADERS },
      );

      if (Array.isArray(data.results)) {
        setSubCustomers((prev) => ({
          ...prev,
          [customerId]: data.results,
        }));
      }
    } catch (error) {
      console.error("Error fetching subcustomers:", error);
    }
  };

  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    setSelectedSubCustomer(null);
    if (selectedOption) {
      fetchSubCustomersTec(selectedOption.value);
    }
  };

  const handleSubCustomerChange = (selectedOption) => {
    setSelectedSubCustomer(selectedOption);
  };

  const handleAddServiceExpense = async (data) => {
    try {
      setLoading(true);

      const formData = {
        technician: technicianId,
        material_required: data.material_required,
      };

      if (requestType === "ticket") {
        formData.ticket = data.ticket;
      } else if (requestType === "customer") {
        formData.customerDetails = selectedCustomer?.value || null;
        if (selectedSubCustomer) {
          formData.subCustomerDetails = selectedSubCustomer.value;
        }
      }

      if (currentStatus === "Approved") {
        formData.approved_remark = data.approved_remark;
      }

      if (currentStatus === "Dispatched") {
        formData.courier_contact_number = data.courier_contact_number || null;
        formData.dockt_no = data.dockt_no || null;
        formData.hub_address = data.hub_address || null;
        formData.dispatched_remark = data.dispatched_remark || "";
        formData.where_to_dispatched = data.where_to_dispatched || null;
      }

      if (serviceExpenseData) {
        await axios.put(
          `${BASE_URL}/api/service-requests/${serviceExpenseData.id}/`,
          formData,
          { headers: HEADERS },
        );
      } else {
        await axios.post(`${BASE_URL}/api/service-requests/`, formData, {
          headers: HEADERS,
        });
      }

      toast.success(
        `Material Request ${
          serviceExpenseData ? "updated" : "added"
        } successfully`,
      );
      handleClose();
      fetchServiceRequests();
    } catch (error) {
      toast.error(error.response?.data?.error || "Error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (data) => {
    const statusData = { status: data.status };

    if (data.status === "Approved") {
      statusData.approved_remark = data.approved_remark || "";
    }

    if (data.status === "Dispatched") {
      statusData.courier_contact_number = data.courier_contact_number || null;
      statusData.dockt_no = data.dockt_no || null;
      statusData.hub_address = data.hub_address || null;
      statusData.dispatched_remark = data.dispatched_remark || "";
      statusData.where_to_dispatched = data.where_to_dispatched || null;
    }

    try {
      await axios.put(
        `${BASE_URL}/api/service-requests/${serviceExpenseData.id}/`,
        statusData,
        { headers: HEADERS },
      );

      handleClose();
      toast.success("Status updated successfully.");
      fetchServiceRequests();
    } catch (error) {
      console.error(error.response?.data?.error || "Error. Please try again.");
    }
  };

  const onSubmit = (data) => {
    if (isStatusUpdate) {
      handleStatusUpdate(data);
    } else {
      handleAddServiceExpense(data);
    }
  };

  const ticketOptions = tickets.map((ticket) => ({
    value: ticket.id,
    label: ticket.id,
  }));

  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: c.customer_name || `Customer ${c.id}`,
  }));

  const subCustomerOptions = subCustomers[selectedCustomer?.value]
    ?.filter((subCustomer) => subCustomer.is_active === true)
    .map((subCustomer) => ({
      value: subCustomer?.id,
      label: subCustomer?.customer_name || "None",
    }));

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isStatusUpdate
              ? "Update Status"
              : serviceExpenseData
                ? "Edit Material Details"
                : "Add Material Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isStatusUpdate ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  {...register("status", { required: true })}
                  value={currentStatus}
                  onChange={(e) => setCurrentStatus(e.target.value)}
                >
                  <option value="Submitted">Submitted</option>
                  <option value="Approved">Approved</option>
                  <option value="Dispatched">Dispatched</option>
                </Form.Select>
              </Form.Group>
              {currentStatus === "Approved" && (
                <Form.Group className="mb-3">
                  <Form.Label>Approved Remark</Form.Label>
                  <Form.Control
                    {...register("approved_remark")}
                    placeholder="Approved remark"
                    defaultValue={serviceExpenseData?.approved_remark || ""}
                  />
                </Form.Group>
              )}
              {currentStatus === "Dispatched" && (
                <>
                  <Row>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Courier Contact Number*</Form.Label>
                        <Form.Control
                          {...register("courier_contact_number")}
                          placeholder="Enter courier contact number"
                          defaultValue={
                            serviceExpenseData?.courier_contact_number || ""
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Dockt No*</Form.Label>
                        <Form.Control
                          {...register("dockt_no")}
                          placeholder="Enter dockt no"
                          defaultValue={serviceExpenseData?.dockt_no || ""}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Hub Address*</Form.Label>
                        <Form.Control
                          {...register("hub_address")}
                          placeholder="Enter hub address"
                          defaultValue={serviceExpenseData?.hub_address || ""}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Where to Dispatched*</Form.Label>
                        <Form.Control
                          {...register("where_to_dispatched")}
                          placeholder="Enter dispatch location"
                          defaultValue={
                            serviceExpenseData?.where_to_dispatched || ""
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Remark*</Form.Label>
                    <Form.Control
                      {...register("dispatched_remark")}
                      placeholder="Enter remark"
                      defaultValue={serviceExpenseData?.dispatched_remark || ""}
                    />
                  </Form.Group>
                </>
              )}
            </>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Request Type</Form.Label>
                <div>
                  <Form.Check
                    inline
                    label="Ticket"
                    type="radio"
                    value="ticket"
                    checked={requestType === "ticket"}
                    onChange={() => setRequestType("ticket")}
                  />
                  <Form.Check
                    inline
                    label={labels.customer}
                    type="radio"
                    value="customer"
                    checked={requestType === "customer"}
                    onChange={() => setRequestType("customer")}
                  />
                </div>
              </Form.Group>

              {requestType === "ticket" && (
                <div className="mb-3">
                  <Form.Label htmlFor="ticket">Ticket ID*</Form.Label>
                  <Controller
                    name="ticket"
                    control={control}
                    rules={{ required: "Ticket is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={ticketOptions}
                        placeholder="Select a ticket ID"
                        value={
                          ticketOptions.find(
                            (option) => option.value === field.value,
                          ) || null
                        }
                        onChange={(option) =>
                          field.onChange(option?.value || "")
                        }
                      />
                    )}
                  />
                  {errors.ticket && (
                    <div className="text-danger">{errors.ticket.message}</div>
                  )}
                </div>
              )}

              {requestType === "customer" && (
                <>
                  <div className="mb-3">
                    <Form.Label htmlFor="customerDetails">
                      {labels.customerName}*
                    </Form.Label>
                    <Controller
                      name="customer"
                      control={control}
                      rules={{ required: "Customer is required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder={labels.customerName}
                          options={customerOptions}
                          value={
                            customerOptions.find(
                              (option) => option.value === field.value,
                            ) || null
                          }
                          onChange={(option) => {
                            field.onChange(option?.value || "");
                            handleCustomerChange(option);
                          }}
                        />
                      )}
                    />
                    {errors.customerDetails && (
                      <div className="text-danger">
                        {errors.customerDetails.message}
                      </div>
                    )}
                  </div>

                  {subCustomerOptions?.length > 0 && (
                    <div className="mb-3">
                      <Form.Label htmlFor="subCustomerDetails">
                        {labels.subCustomerName}*
                      </Form.Label>
                      <Controller
                        name="subCustomerDetails"
                        control={control}
                        rules={{ required: "SubCustomer is required" }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder={labels.subCustomerName}
                            options={subCustomerOptions}
                            value={
                              subCustomerOptions?.find(
                                (option) => option.value === field.value,
                              ) || null
                            }
                            onChange={(option) => {
                              field.onChange(option?.value || "");
                              handleSubCustomerChange(option);
                            }}
                            isClearable
                          />
                        )}
                      />
                      {errors.subCustomerDetails && (
                        <div className="text-danger">
                          {errors.subCustomerDetails.message}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="mb-3">
                <Form.Label>Material Required</Form.Label>
                {fields.map((field, index) => (
                  <Row key={field.id} className="mb-2 align-items-center">
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                      <Controller
                        name={`material_required.${index}.stock_id`}
                        control={control}
                        rules={{ required: "Material is required" }}
                        render={({ field: selectField }) => (
                          <Select
                            value={
                              stocks
                                ?.map((s) => ({
                                  value: s.id,
                                  label: s.item || "Unnamed Material",
                                }))
                                .find((o) => o.value === selectField.value) ||
                              null
                            }
                            options={stocks?.map((s) => ({
                              value: s.id,
                              label: s.item || "Unnamed Material",
                            }))}
                            placeholder="Select material"
                            onChange={(option) => {
                              selectField.onChange(option.value); 
                              setValue(
                                `material_required.${index}.name`,
                                option.label,
                              );
                            }}
                          />
                        )}
                      />
                      {errors?.material_required?.[index]?.stock_id && (
                        <div className="text-danger small">
                          {errors.material_required[index].stock_id.message}
                        </div>
                      )}
                    </Col>

                    <Col xs={8} md={4} className="mb-2 mb-md-0">
                      <Form.Control
                        type="number"
                        min="1"
                        {...register(`material_required.${index}.quantity`, {
                          required: "Quantity is required",
                          valueAsNumber: true,
                          min: {
                            value: 1,
                            message: "Quantity must be at least 1",
                          },
                        })}
                        placeholder="Qty"
                      />
                      {errors?.material_required?.[index]?.quantity && (
                        <div className="text-danger small">
                          {errors.material_required[index].quantity.message}
                        </div>
                      )}
                    </Col>

                    <Col xs={4} md={2} className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => remove(index)}
                        disabled={fields.length === 1}
                        className="d-flex align-items-center justify-content-center w-100"
                      >
                        ✖
                      </Button>
                    </Col>
                  </Row>
                ))}

                <Button
                  variant="primary"
                  onClick={() => append({ name: "", quantity: 1 })}
                  className="mt-2 w-100"
                >
                  + Add Material
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            className="d-flex align-items-center justify-content-center gap-2"
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : isStatusUpdate ? (
              "Update Status"
            ) : (
              "Submit"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ServiceExpenseModal;
